import { createAction } from '@reduxjs/toolkit';
import { type ShipmentType } from '../../helpers/klarna';
import { ERRORS, Method, STATES, STEPS, State } from '../../constants/checkoutConstants';
import get from 'lodash.get';
import { trackCompletedStep } from '../../helpers/checkoutTracking';
import { checkoutShippingActions_viewer$data as TViewer } from '../__generated__/checkoutShippingActions_viewer.graphql';
import { checkoutShippingActions_transaction$data as TTransaction } from '../__generated__/checkoutShippingActions_transaction.graphql';
import { AddressType } from '../../payment/bankAccount/CheckoutBankAccountsSubmit';
import { SaveKlarnaPaymentPayload } from '../checkoutKlarnaActions';

export const toggleShipmentNotesAction = createAction('CHECKOUT_TOGGLE_SHIPMENT_NOTES');
export const navigateDeliveryAction = createAction('CHECKOUT_NAVIGATE_DELIVERY');
export const shipmentQuoteSelectedAction = createAction<{
    shipmentType: ShipmentType;
    isCustomQuoteSelected: boolean;
    shipmentQuoteId: string;
}>('CHECKOUT_QUOTE_SELECTED');
export const setShipmentNotesAction = createAction<{
    shipmentNotes: string;
} | null>('CHECKOUT_SET_SHIPMENT_NOTES');
export const offerPriceBookNameChangeAction = createAction(
    'OFFER_PRICE_BOOK_NAME_CHANGE',
    (payload: { offerPriceBookName: string }) => {
        return { payload };
    }
);
export const offerAmountPerItemChangeAction = createAction(
    'OFFER_AMOUNT_PER_ITEM_CHANGE',
    (payload: { newOffer: number }) => {
        return { payload };
    }
);
export const transactionTypeChangeAction = createAction(
    'TRANSACTION_TYPE_CHANGE',
    (payload: { transactionType: string }) => {
        return { payload };
    }
);
export const addressSelectedAction = createAction(
    'CHECKOUT_ADDRESS_SELECTED',
    (payload: {
        shippingState?: (typeof STATES)[keyof typeof STATES];
        shippingAddress: unknown;
        paymentMethodToken: unknown;
        isEditingPartial?: unknown;
    }) => {
        const { shippingState, shippingAddress, paymentMethodToken, isEditingPartial } = payload;
        const isAdding = shippingState === STATES.STATE_NEW;

        return {
            payload: {
                shippingState,
                shippingAddress,
                paymentMethodToken,
                usePreFilledAddress: isAdding && !!get(shippingAddress, 'serviceId'),
                isEditingPartial,
            },
        };
    }
);
export const transactionBuyerChangedAction = createAction<{
    buyerId: string;
    isNewUser?: boolean;
    address?: {
        serviceId: string | null;
    };
}>('TRANSACTION_BUYER_CHANGED');
export const shippingCompletedAction = createAction(
    'CHECKOUT_SHIPPING_COMPLETED',
    (payload: {
        viewer: TViewer;
        transaction: TTransaction;
        newAddressId?: string | null;
        shippingAddressId: string;
    }) => {
        const { viewer, transaction, newAddressId, shippingAddressId } = payload;
        const { restrictionsEnabled } = viewer;
        const { isCheckoutBlocked } = get(transaction, 'warnings') || {};

        trackCompletedStep(STEPS.STEP_SHIPPING);

        return {
            payload: {
                shippingAddressId: newAddressId || shippingAddressId,
                isCheckoutBlocked: !!(restrictionsEnabled && isCheckoutBlocked),
            },
        };
    }
);
export const registrationErrorAction = createAction<{
    errors: {
        [key: string]: string;
    };
}>('CHECKOUT_REGISTRATION_ERROR');
export const setProcessingErrorAction = createAction<{
    errorStatus: (typeof ERRORS)[keyof typeof ERRORS] | null;
    errorMessage: string | null;
}>('CHECKOUT_PROCESSING_ERROR');
export const setUserMismatchErrorAction = createAction<{
    userMismatch: boolean;
}>('CHECKOUT_USER_MISMATCH_ERROR');
export const emptyShippingAddressAction = createAction('CHECKOUT_EMPTY_SHIPPING_ADDRESS');
export const setQuantityAction = createAction<{
    quantity: number;
    paymentMethodToken: string;
}>('TRANSACTION_SET_QUANTITY');
export const emptyPaymentMethodAction = createAction<{
    method: Method;
    creditCardState?: State;
    lastValidCreditCardToken?: string;
    bankAccountState?: State;
}>('CHECKOUT_EMPTY_PAYMENT_METHOD');
export const saveCreditCardAction = createAction<{
    token: string;
    paymentProcessor: string;
}>('CHECKOUT_SAVE_CREDITCARD');
export const saveBankAccountAction = createAction<{
    token: string;
    paymentProcessor: string;
}>('CHECKOUT_SAVE_BANKACCOUNT');
export const savePaypalAction = createAction<{
    token: string;
}>('CHECKOUT_SAVE_PAYPAL');
export const saveCheckAction = createAction('CHECKOUT_SAVE_CHECK');
export const saveWireTransferAction = createAction<{
    billingAddress: AddressType;
}>('CHECKOUT_SAVE_WIRE_TRANSFER');
export const saveOnPlatformWireTransferAction = createAction<{
    billingAddress: AddressType;
}>('CHECKOUT_SAVE_ON_PLATFORM_WIRE_TRANSFER');
export const saveApplePayAction = createAction('CHECKOUT_SAVE_APPLE_PAY');
export const saveKlarnaPaymentMethodAction =
    createAction<SaveKlarnaPaymentPayload>('CHECKOUT_SAVE_KLARNA');
export const submitKlarnaError = createAction('KLARNA_SUBMIT_ERROR');
export const completeCreditCardAction = createAction<{
    paymentMethodToken: string;
}>('CHECKOUT_COMPLETE_CREDITCARD');
export const completeBankAccountAction = createAction<{
    paymentMethodToken: string;
    paymentProcessor: string;
}>('CHECKOUT_COMPLETE_BANKACCOUNT');
export const saveStripePaymentMethodApiType = createAction<{
    paymentMethodApiType: string;
}>('CHECKOUT_SAVE_PAYMENT_METHOD_API_TYPE');
export const setPaymentProcessor = createAction<{
    paymentProcessor: string;
}>('CHECKOUT_SET_PAYMENT_PROCESSOR');
export const navigateShippingAction = createAction('CHECKOUT_NAVIGATE_SHIPPING');
export const navigatePaymentAction = createAction('CHECKOUT_NAVIGATE_PAYMENT');
export const navigateReviewAction = createAction('CHECKOUT_NAVIGATE_REVIEW');
export const savePaymentMethod = createAction<{
    paymentMethodType: string;
    paymentProcessor: string;
    paymentMethodToken?: string;
}>('CHECKOUT_SAVE_PAYMENT_METHOD');
